import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, ArrowLeft, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import QuoteBanner from "@/components/QuoteBanner";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";

export default function Contact() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", contact_id: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | ok | err
  const [err, setErr] = useState("");

  function onChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    setErr("");
    try {
      await api.post("/contact", form);
      setStatus("ok");
      setForm({ email: "", contact_id: "", message: "" });
    } catch (e) {
      setStatus("err");
      setErr(e?.response?.data?.detail?.[0]?.msg || e?.message || "Failed");
    }
  }

  const ta = lang === "ta";

  return (
    <div className="App">
      <Header compact />
      <QuoteBanner />

      <main className="max-w-2xl mx-auto px-6 pt-6 pb-24">

        <button
          onClick={() => navigate(-1)}
          className={`mt-6 mb-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#1a1640] shadow-sm hover:bg-[#f3eedc] active:scale-[0.99] transition ${
            ta ? "font-tamil" : ""
          }`}
          data-testid="contact-back-btn"
        >
          <ArrowLeft size={16} strokeWidth={2.4} />
          {t("contact_back")}
        </button>

        <h1
          className={`text-3xl sm:text-4xl font-semibold text-[#1a1640] tracking-tight ${
            ta ? "font-tamil" : "font-display"
          }`}
          data-testid="contact-title"
        >
          {t("contact_title")}
        </h1>
        <p className={`mt-2 text-[#6b6894] text-[15px] ${ta ? "font-tamil" : ""}`}>
          {t("contact_sub")}
        </p>

        {status === "ok" ? (
          <div
            className="mt-8 rounded-[22px] bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-3"
            data-testid="contact-success"
          >
            <CheckCircle2 size={24} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className={`font-semibold text-emerald-800 ${ta ? "font-tamil" : ""}`}>
                {t("contact_success_title")}
              </div>
              <div className={`text-emerald-700 text-[14px] mt-1 ${ta ? "font-tamil" : ""}`}>
                {t("contact_success_sub")}
              </div>
              <button
                onClick={() => setStatus("idle")}
                className={`mt-3 text-sm font-semibold text-emerald-800 hover:text-emerald-950 underline ${
                  ta ? "font-tamil" : ""
                }`}
                data-testid="contact-send-another"
              >
                {t("contact_send_another")}
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mt-8 bg-white rounded-[22px] p-6 space-y-4 shadow-sm"
            data-testid="contact-form"
          >
            <Field
              label={t("contact_email")}
              name="email"
              type="email"
              required
              value={form.email}
              onChange={onChange}
              placeholder="you@example.com"
              ta={ta}
              testid="contact-email-input"
            />
            <Field
              label={t("contact_contact_id")}
              name="contact_id"
              required
              value={form.contact_id}
              onChange={onChange}
              placeholder={t("contact_contact_id_placeholder")}
              ta={ta}
              testid="contact-contactid-input"
            />
            <Field
              label={t("contact_message")}
              name="message"
              required
              as="textarea"
              value={form.message}
              onChange={onChange}
              placeholder={t("contact_message_placeholder")}
              ta={ta}
              testid="contact-message-input"
            />

            {status === "err" && (
              <div className="text-sm text-rose-600 font-semibold" data-testid="contact-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="myneta-btn w-full disabled:opacity-60"
              data-testid="contact-submit-btn"
              style={{ padding: "12px 18px", fontSize: 14 }}
            >
              <Send size={15} strokeWidth={2.4} />
              {status === "sending" ? t("contact_sending") : t("contact_submit")}
            </button>
          </form>
        )}
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, required, type, as, ta, testid }) {
  const Tag = as === "textarea" ? "textarea" : "input";
  return (
    <label className="block">
      <div
        className={`text-[12px] uppercase tracking-widest text-[#6b6894] font-semibold mb-1.5 ${
          ta ? "font-tamil normal-case tracking-normal" : ""
        }`}
      >
        {label}
      </div>
      <Tag
        name={name}
        type={type || "text"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        rows={as === "textarea" ? 5 : undefined}
        className="w-full rounded-xl border border-slate-200 bg-[#faf7ea] px-3.5 py-2.5 text-[15px] text-[#1a1640] placeholder:text-[#a09dbf] outline-none focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 transition"
        data-testid={testid}
      />
    </label>
  );
}
